import { useState } from 'react'
import { useSaveLeadDetails } from '../../api/queries/dealMutations'
import { userFacingMessage } from '../../api/errors'
import { Button } from '../../ui/Button'
import { Select } from '../../ui/Select'
import { TextField } from '../../ui/TextField'
import { COUNTRIES } from '../../lib/countries'
import type { DealDetail } from '../../api/adapters/dealDetail'
import type { Participant } from '../../api/types'
import styles from './DealPanel.module.css'

/**
 * Editing the lead: company, country, email, and the people on the calls.
 *
 * One form, one Save, two endpoints (see useSaveLeadDetails). Confirming a
 * decision maker here is what establishes authority, so stage, priority and
 * recommendation are recomputed by the backend on save and read back — this
 * screen predicts none of them.
 *
 * The draft is component state, discarded on cancel. Validation is the
 * backend's: an empty company name comes back as a 422 and is shown below,
 * rather than the same rule being written a second time in the browser.
 */
const COUNTRY_OPTIONS = [{ value: '', label: 'Not set' }, ...COUNTRIES.map((c) => ({ value: c, label: c }))]

const JUDGEMENT_OPTIONS = [
  { value: '', label: 'Not reviewed' },
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
  { value: 'unclear', label: 'Not sure' },
]

export function LeadDetailsForm({ deal, onDone }: { deal: DealDetail; onDone: () => void }) {
  const [companyName, setCompanyName] = useState(deal.companyName)
  const [country, setCountry] = useState(deal.country ?? '')
  const [email, setEmail] = useState(deal.contactEmail ?? '')
  const [people, setPeople] = useState<Participant[]>(deal.participants.map((person) => ({ ...person })))
  const save = useSaveLeadDetails(deal.id)

  function updatePerson(index: number, patch: Partial<Participant>) {
    setPeople((current) => current.map((person, i) => (i === index ? { ...person, ...patch } : person)))
  }

  function submit() {
    save.mutate(
      {
        identity: { company_name: companyName, country: country || null, contact_email: email || null },
        participants: people,
      },
      { onSuccess: onDone },
    )
  }

  return (
    <div className={styles.form}>
      <TextField label="Company" value={companyName} onChange={(e) => setCompanyName(e.target.value)} />
      <Select
        label="Location (country)"
        value={country}
        options={COUNTRY_OPTIONS}
        onChange={(e) => setCountry(e.target.value)}
      />
      <TextField label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />

      <fieldset className={styles.people2}>
        <legend className={styles.legend}>People on the calls</legend>
        {people.length === 0 ? <p className={styles.absent}>No people yet.</p> : null}
        {people.map((person, index) => (
          <div key={index} className={`${styles.personRow} ${person.decision_maker ? '' : styles.pending}`}>
            <div className={styles.personFields}>
              <TextField
                label={`Name of person ${index + 1}`}
                hideLabel
                placeholder="Name"
                value={person.name}
                onChange={(e) => updatePerson(index, { name: e.target.value })}
              />
              <TextField
                label={`Title of person ${index + 1}`}
                hideLabel
                placeholder="Title"
                value={person.role ?? ''}
                onChange={(e) => updatePerson(index, { role: e.target.value })}
              />
            </div>
            <div className={styles.personJudge}>
              <span aria-hidden="true">Decision maker:</span>
              <Select
                label={`Is ${person.name || `person ${index + 1}`} the decision maker?`}
                hideLabel
                options={JUDGEMENT_OPTIONS}
                value={person.decision_maker ?? ''}
                onChange={(e) =>
                  updatePerson(index, {
                    decision_maker: (e.target.value || null) as Participant['decision_maker'],
                  })
                }
              />
              <Button
                aria-label={`Remove ${person.name || `person ${index + 1}`}`}
                onClick={() => setPeople((current) => current.filter((_, i) => i !== index))}
              >
                Remove
              </Button>
            </div>
          </div>
        ))}
        <Button
          onClick={() =>
            setPeople((current) => [...current, { name: '', role: '', decision_maker: null, detected: false }])
          }
        >
          Add person
        </Button>
      </fieldset>

      <div className={styles.editActions}>
        <Button variant="primary" onClick={submit} disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
        <Button onClick={onDone} disabled={save.isPending}>
          Cancel
        </Button>
      </div>

      {save.isError ? (
        <p className={styles.failed} role="alert">
          These details were not saved. {userFacingMessage(save.error)}
        </p>
      ) : null}
    </div>
  )
}
